import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { useAppSelector, useWindowDimensions } from 'src/hooks';
import { Grid, GridProps, Skeleton, Typography } from '@mui/material';
import { Box } from '@mui/system';
import {
  IGridToggleData,
  IGridXToYResponse,
  KeyOfToggle
} from '../../../planner_v2/types/grid-x-to-y-response.model';
import './grid-table-component.scss';
import GridDatumBox from './grid-datum-box';
import { ProductSelection } from '../../../constants/constant';
import {
  IColorGradientMapDetails,
  ToBeMetric
} from '../../types/grid-color-gradient-map.model';

interface IGridTableComponentProps {
  toggle: KeyOfToggle;
  /**
   * Default true - Whether to set height responsively when dimension of window changes
   * If false, then height is set to 100%
   */
  // eslint-disable-next-line react/require-default-props
  setResponsiveHeight?: boolean;
  /**
   * Header row (first row of the grid)
   */
  headerRow: string[];
  /**
   * Details for the grid to render grid layout
   */
  gridData: IGridXToYResponse;
  /**
   * Boolean to show the progress bar
   */
  fetchingData: boolean;

  // eslint-disable-next-line react/require-default-props
  toBeMetrics?: ToBeMetric[];
}

/**
 * The static header, element positioned at 00 in grid matrix
 */
function StaticHeaderBox({
  content
}: {
  /**
   * The content displayed in the cell
   */
  content: string;
}) {
  return (
    <Grid
      container
      className='sticky-header'
      justifyContent='center'
      alignItems='center'
      sx={{
        p: 1,
        border: '1px solid #D9D9D9',
        color: '#8080CF',
        backgroundColor: '#F9F9F9'
      }}
    >
      <Typography fontWeight={300} fontSize='8px'>
        {content}
      </Typography>
    </Grid>
  );
}

/**
 * Renders the layout of the headers rows and columns (brand names and price bands)
 */
function HeaderBox({
  title,
  gridProps = {}
}: {
  /**
   * Content displayed in the grid cell
   */
  title: string;
  /**
   * Additional props for the wrapping `Grid` component
   */
  gridProps: GridProps;
}) {
  return (
    <Grid
      container
      justifyContent='center'
      alignItems='center'
      sx={{ p: 1, border: '1px solid #D9D9D9', backgroundColor: '#F2F2F2' }}
      {...gridProps}
    >
      <Typography noWrap fontWeight={700} fontSize='10px'>
        {title}
      </Typography>
    </Grid>
  );
}

interface IGridWrapperProps extends PropsWithChildren {
  gridRef: React.MutableRefObject<HTMLDivElement>;
  columns: any[];
  rows: any[];
  gridHeight: string;
  responsiveHeight: boolean;
}

function GridWrapper({
  children,
  columns,
  gridRef,
  rows,
  gridHeight,
  responsiveHeight = true
}: IGridWrapperProps) {
  return (
    <Box
      ref={gridRef}
      sx={{
        display: 'grid',
        overflowY: 'auto',
        maxHeight: gridHeight,
        gridTemplateColumns: `[y-column] 100px ${columns
          .map(() => '1fr')
          .join(' ')}`,
        gridTemplateRows: `[x-column] auto ${rows
          .map(() => (responsiveHeight ? '1fr' : '250px'))
          .join(' ')}`
      }}
    >
      {children}
    </Box>
  );
}

/**
 * The root grid component for the planner
 */
export default function GridTableComponentV2({
  setResponsiveHeight = true,
  toggle,
  headerRow,
  gridData,
  fetchingData,
  toBeMetrics = []
}: IGridTableComponentProps) {
  const { height: windowHeight, width } = useWindowDimensions();
  const { currentMetricToggle } = useAppSelector((state) => state.asIsToBeV2);
  const toBeMetric = toBeMetrics.find(
    (metric) => metric.label === currentMetricToggle?.label
  );
  /**
   * An array of matrix representing how the grid will look (columns and rows) along with
   * the content shown at each of the grid cell
   */
  const gridMatrix = [
    ...gridData.row_headers.map((rowHeader, index) => [
      rowHeader,
      ...gridData.grid_data[index]
    ])
  ];
  const loadingGridMatrix = [
    [1, 2, 3, 4],
    [5, 6, 7, 8],
    [9, 10, 11, 12]
  ];
  /**
   * The default height of the grid.
   */
  const [gridHeight, setGridHeight] = useState('100%');
  const gridRef = useRef<HTMLDivElement>();
  /**
   * Sets the height of the grid as per the available space on the screen until footer
   */
  useEffect(() => {
    setTimeout(() => {
      if (gridRef.current && setResponsiveHeight) {
        try {
          const grid = gridRef.current;
          const height = windowHeight - (grid.offsetTop + 50);
          if (height < 0) {
            throw Error('Negative height computed for grid');
          }
          setGridHeight(`${height}px`);
        } catch (e) {
          console.error('Error while setting grid height', e);
          setGridHeight('100%');
        }
      }
    }, 500);
  }, [gridData, windowHeight, width]);
  if (fetchingData) {
    return (
      <Grid container spacing={1}>
        {loadingGridMatrix.map((rows) => (
          <Grid item xs={12} container spacing={1}>
            {rows.map((row) => (
              <Grid key={row} item xs sx={{ height: '170px' }}>
                <Skeleton sx={{ width: '100%', height: '100%' }} />
              </Grid>
            ))}
          </Grid>
        ))}
      </Grid>
    );
  }
  return (
    <GridWrapper
      gridHeight={gridHeight}
      columns={gridData.column_headers}
      rows={gridData.row_headers}
      gridRef={gridRef}
      responsiveHeight={setResponsiveHeight}
    >
      {headerRow.map((rowItem, rowIndex) => {
        if (rowIndex === 0) {
          return (
            <StaticHeaderBox
              key={rowItem as string}
              content={rowItem as string}
            />
          );
        }
        return (
          <HeaderBox
            key={rowItem as string}
            gridProps={{
              className: 'sticky-header'
            }}
            title={rowItem as string}
          />
        );
      })}
      {gridMatrix.map((rows) =>
        rows.map((item, columnIndex) => {
          if (columnIndex === 0) {
            return (
              <HeaderBox
                key={item as string}
                gridProps={{
                  className: 'row-item'
                }}
                title={item as string}
              />
            );
          }
          return (
            <GridDatumBox
              toBeMetric={toBeMetric}
              items={(item as IGridToggleData)[toggle]}
            />
          );
        })
      )}
    </GridWrapper>
  );
}
